import React, { useState, useEffect, ChangeEvent } from "react";
import {
    Typography,
    TextField,
    Box,
    MenuItem,
    Select,
    SelectChangeEvent,
    FormControl,
    InputLabel,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
    ThemeProvider,
    Theme,
    StyledEngineProvider,
    createTheme,
} from "@mui/material";
import { SecondaryButton, DestructiveButton } from "./../../components/layout/Button/Button";
import { useClassroom } from "./contexts/classroom.context";
import { verbalTheme } from "../layout/themes/verbal.theme";
import { EmotionTimeframe } from "./entities/EmotionTimeframe";

declare module "@mui/styles/defaultTheme" {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

interface ThresholdSettings {
    emotionCount: number;
    timeframeFrequency: number;
    timeframeUnit: EmotionTimeframe;
}

export const ManageThresholdScreen = (): JSX.Element => {
    const [thresholdSettings, setThresholdSettings] = useState<ThresholdSettings | null>(null);
    const [showDialog, setShowDialog] = useState(false);

    const { updateEmotionThreshold, getEmotionThreshold, classroom, threshold } = useClassroom();
    const [emotionCount, setEmotionCount] = useState<number | null>(null);
    const [timeframeFrequency, setTimeframeFrequency] = useState<number | null>(null);
    const [timeframeUnit, setTimeframeUnit] = useState<EmotionTimeframe | null>(null);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        if (classroom) {
            getEmotionThreshold(classroom.id).finally(() => setLoading(false));
        }
    }, [classroom, getEmotionThreshold]);

    useEffect(() => {
        if (threshold) {
            setEmotionCount(threshold.emotionCount);
            setTimeframeFrequency(threshold.emotionFrequency);
            setTimeframeUnit(threshold.emotionTimeframe as EmotionTimeframe);
        }
    }, [threshold]);

    if (loading) {
        return <CircularProgress />;
    }

    const handleSubmitButton = () => {
        if (emotionCount !== null && timeframeFrequency !== null && timeframeUnit !== null) {
            const newSettings: ThresholdSettings = {
                emotionCount,
                timeframeFrequency,
                timeframeUnit,
            };
            setThresholdSettings(newSettings);
            setShowDialog(true);
        }
    };

    const handleConfirmButton = () => {
        if (classroom && thresholdSettings) {
            updateEmotionThreshold(
                classroom.id,
                thresholdSettings?.emotionCount,
                thresholdSettings?.timeframeFrequency,
                thresholdSettings?.timeframeUnit,
            );
        }
        setShowDialog(false);
        setEditMode(false);
    };

    const handleEditButton = () => {
        setEditMode(true);
    };

    const handleCancelButton = () => {
        setEditMode(false);
    };

    const theme = createTheme({
        ...verbalTheme,
        components: {
            ...verbalTheme.components,
            MuiSelect: {
                styleOverrides: {
                    select: {
                        height: "1.5em",
                        verticalAlign: "middle",
                        fontFamily: "Montserrat",
                        fontSize: "1.2em",
                    },
                },
            },
        },
    });

    return (
        <div>
            <Dialog open={showDialog !== false} onClose={() => setShowDialog(false)}>
                <DialogTitle>{"Confirm Update"}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to change your emotion threshold settings?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDialog(false)} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            handleConfirmButton();
                        }}
                        color="primary"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <Box
                padding={2}
                component="form"
                style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "20px",
                    height: "200%",
                    contain: "layout style paint",
                }}
            >
                <Typography variant="h4" component="p">
                    I would like to be notified if a child has inputted
                    {editMode ? (
                        <TextField
                            variant="outlined"
                            value={emotionCount?.toString() || ""}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmotionCount(Number(e.target.value))}
                            type="number"
                            inputProps={{ min: 0, max: 20, style: { fontSize: "1.2em", padding: "8px" } }}
                            style={{ width: "3em", verticalAlign: "middle", margin: "0 8px" }}
                        />
                    ) : (
                        " " + emotionCount?.toString() + " "
                    )}
                    negative emotions within a timeframe of
                    {editMode ? (
                        <TextField
                            variant="outlined"
                            value={timeframeFrequency?.toString() || ""}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setTimeframeFrequency(Number(e.target.value))
                            }
                            type="number"
                            inputProps={{ min: 0, max: 20, style: { fontSize: "1.2em", padding: "8px" } }}
                            style={{ width: "3em", verticalAlign: "middle", margin: "0 8px" }}
                        />
                    ) : (
                        " " + timeframeFrequency?.toString() + " "
                    )}
                    {editMode ? (
                        <StyledEngineProvider injectFirst>
                            <ThemeProvider theme={theme}>
                                <FormControl variant="outlined">
                                    <InputLabel id="timeframe-unit-label"></InputLabel>
                                    <Select
                                        variant="outlined"
                                        labelId="timeframe-unit-label"
                                        id="timeframe-unit-select"
                                        value={timeframeUnit || ""}
                                        onChange={(e: SelectChangeEvent<EmotionTimeframe>) =>
                                            setTimeframeUnit(e.target.value as EmotionTimeframe)
                                        }
                                        style={{ height: "2.3em" }}
                                        displayEmpty
                                    >
                                        {Object.entries(EmotionTimeframe).map(([value, label]) => {
                                            return (
                                                <MenuItem key={label} value={label}>
                                                    {value}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </ThemeProvider>
                        </StyledEngineProvider>
                    ) : (
                        timeframeUnit
                    )}
                </Typography>
            </Box>
            {editMode ? (
                <Box>
                    <SecondaryButton onClick={() => handleSubmitButton()} style={{ marginRight: "0.6em" }}>
                        Confirm
                    </SecondaryButton>
                    <DestructiveButton onClick={() => handleCancelButton()}>Cancel</DestructiveButton>
                </Box>
            ) : (
                <SecondaryButton onClick={() => handleEditButton()}>Edit</SecondaryButton>
            )}
        </div>
    );
};
